/**
 * What GroupChat shows when a chat image fails to load (EN-1553).
 *
 * GET /chat/attachments returns 404 when the attachment is no longer
 * visible or its blob is gone, 403 { code: 'chat_suspended' } for a
 * suspended user, and 5xx when storage is unavailable. Only the last (and
 * network errors or timeouts) can succeed on a retry; for chat suspension
 * a retry works once access is restored, so it keeps its button as before.
 */

export interface ChatImageErrorState {
  message: string;
  canRetry: boolean;
}

export const CHAT_IMAGE_UNAVAILABLE_MESSAGE =
  'This image is no longer available.';
/** Same text as chatErrorMessage in GroupChatService for chat_suspended. */
const CHAT_SUSPENDED_MESSAGE =
  'Your chat access is suspended. Your other features remain available.';
/** Error bodies are tiny JSON objects; never read a large blob. */
const MAX_ERROR_BODY_BYTES = 16 * 1024;

interface ErrorResponse {
  status?: number;
  data?: unknown;
}

function errorResponse(error: unknown): ErrorResponse | undefined {
  return (error as { response?: ErrorResponse } | null | undefined)?.response;
}

/**
 * Maps an image load error to its message and whether Retry can help, or
 * null for a cancelled request (axios marks those with __CANCEL__).
 */
export function chatImageErrorState(
  error: unknown,
  fallback: string
): ChatImageErrorState | null {
  if ((error as { __CANCEL__?: boolean } | null | undefined)?.__CANCEL__)
    return null;
  const response = errorResponse(error);
  const code = (response?.data as { code?: unknown } | null | undefined)?.code;
  if (code === 'chat_suspended')
    return { message: CHAT_SUSPENDED_MESSAGE, canRetry: true };
  if (response?.status === 403 || response?.status === 404)
    return { message: CHAT_IMAGE_UNAVAILABLE_MESSAGE, canRetry: false };
  return { message: fallback, canRetry: true };
}

/**
 * With responseType 'blob' axios leaves a JSON error body as a Blob, so its
 * code cannot be read. Replaces a small JSON Blob body with the parsed
 * object and returns the same error; anything else is left as it was.
 */
export async function withJsonErrorBody<T>(error: T): Promise<T> {
  const response = errorResponse(error);
  const data = response?.data as
    | { type?: unknown; size?: unknown; text?: unknown }
    | null
    | undefined;
  if (
    !response ||
    typeof data?.text !== 'function' ||
    typeof data.type !== 'string' ||
    !data.type.toLowerCase().includes('json') ||
    typeof data.size !== 'number' ||
    data.size > MAX_ERROR_BODY_BYTES
  )
    return error;
  try {
    const body: unknown = JSON.parse(await (data as Blob).text());
    if (body && typeof body === 'object') response.data = body;
  } catch {
    // Unreadable body: keep the status-based mapping.
  }
  return error;
}
